import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';

/**
 * Whether a feedback cycle's configuration may still be changed.
 *
 * DERIVED on every render from the group's own status — never persisted, never latched into state.
 * That choice is the point of this module, not an implementation detail: it is still being
 * arbitrated whether an HR administrator may REOPEN a completed cycle, and anything that recorded
 * "this flow was locked" on the client would have decided that question by accident. A status that
 * moves back off `Completed` re-enables the whole flow on the next render, with nothing to unwind.
 *
 * The same rule already ships for the manager assessment (`useAssessmentReadOnly`), which derives
 * its own flag from its entity's status and hands back a "can undo" beside it. Written as a plain
 * predicate rather than a hook because it returns one boolean and calls no hook of its own —
 * `cycleEvaluationGroupSteps.ts` holds this flow's other derivations from the group in the same
 * form. Kept in its own module so the Dashboard can read it later without dragging that file's
 * step-page import weight along.
 */
export const isCycleEvaluationGroupReadOnly = (group?: CycleEvaluationGroup | null): boolean =>
    group?.status === CycleEvaluationGroupStatus.COMPLETED;
